// ==========================================================================
// screens/MealPlan.tsx — the week's meals (FF-054, 10 Oct 2026)
//   James: "love the weekly meal plan, put that in for sure."
//   Seven days, recipes from the book (or plain words like "Takeaway") on each,
//   and one button that puts the week's ingredients on the shopping list:
//   scaled to how many it's for, joined up when two recipes need the same
//   thing, and leaving out what's already on the list.
// ==========================================================================
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { addDays, addWeeks, format, isSameDay, parseISO, startOfWeek } from "date-fns";
import { ChefHat, ChevronLeft, ChevronRight, Minus, Plus, Search, ShoppingBasket, Trash2, UtensilsCrossed } from "lucide-react";
import Sheet, { Lbl, inputCls } from "@/components/Sheet";
import { ErrorText } from "@/components/ui";
import { tokenColour, ymd } from "@/lib/calendar";
import {
  SLOTS, guessSection, hasTab, scaleQty, useAddShopping, useMarkMealsListed, useMeals, useRecipeTabs, useRecipes, useSaveMeal, useShopping,
  type Meal, type Recipe, type Section, type Slot,
} from "@/lib/kitchen";

const QUICK = ["Takeaway", "Leftovers", "Eating out", "Something easy"];

export default function MealPlan() {
  const [week, setWeek] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const from = ymd(days[0]); const to = ymd(days[6]);
  const meals = useMeals(from, to); const recipes = useRecipes(); const tabs = useRecipeTabs();
  const [edit, setEdit] = useState<(Partial<Meal> & { plan_on: string }) | null>(null);
  const [building, setBuilding] = useState(false);
  const byId = new Map((recipes.data ?? []).map((r) => [r.id, r]));
  const list = meals.data ?? [];
  const dinners = list.filter((m) => m.slot === "dinner").length;
  const toShop = list.filter((m) => m.recipe_id && !m.added_to_list_at).length;
  const thisWeek = isSameDay(week, startOfWeek(new Date(), { weekStartsOn: 1 }));
  const colourOf = (r?: Recipe) => tabs.data?.find((t) => r && hasTab(r, t))?.colour ?? "primary";

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center rounded-full bg-surface p-1 shadow-soft ring-1 ring-line/70">
          <button type="button" onClick={() => setWeek(addWeeks(week, -1))} aria-label="Last week" className="rounded-full p-2 hover:bg-surface-raised"><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[150px] px-2 text-center text-sm font-semibold">{format(days[0], "d MMM")} to {format(days[6], "d MMM")}</span>
          <button type="button" onClick={() => setWeek(addWeeks(week, 1))} aria-label="Next week" className="rounded-full p-2 hover:bg-surface-raised"><ChevronRight className="h-4 w-4" /></button>
        </div>
        {!thisWeek && <button type="button" onClick={() => setWeek(startOfWeek(new Date(), { weekStartsOn: 1 }))} className="text-sm font-semibold text-primary">This week</button>}
        <span className="text-sm text-ink-muted">{dinners} of 7 dinners planned</span>
        <button type="button" onClick={() => setBuilding(true)} disabled={!list.some((m) => m.recipe_id)}
          className="ml-auto inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-ink shadow-soft disabled:opacity-40">
          <ShoppingBasket className="h-4 w-4" />{toShop ? `Shop for this week (${toShop})` : "Shop for this week"}
        </button>
      </div>

      <div className="grid gap-3 md:grid-cols-7">
        {days.map((d) => {
          const key = ymd(d); const today = isSameDay(d, new Date());
          const mine = list.filter((m) => m.plan_on === key).sort((a, b) => SLOTS.findIndex((s) => s.key === a.slot) - SLOTS.findIndex((s) => s.key === b.slot));
          return (
            <div key={key} className={`flex min-w-0 flex-col rounded-2xl p-3 ring-1 md:min-h-[260px] ${today ? "bg-primary/5 ring-primary/40" : "bg-surface ring-line/70"}`}>
              <div className="mb-2 flex items-baseline justify-between gap-2 px-1">
                <span className={`font-semibold ${today ? "text-primary" : ""}`}>{format(d, "EEE")}</span>
                <span className="text-xs text-ink-muted">{today ? "Today" : format(d, "d MMM")}</span>
              </div>
              <div className="flex flex-1 flex-col gap-2">
                {mine.map((m) => {
                  const r = m.recipe_id ? byId.get(m.recipe_id) : undefined;
                  const c = colourOf(r);
                  return (
                    <button key={m.id} type="button" onClick={() => setEdit({ ...m })} className="min-w-0 rounded-xl p-2.5 text-left transition hover:-translate-y-0.5" style={{ background: r ? tokenColour(c, 0.12) : "rgb(var(--surface-raised))" }}>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">{SLOTS.find((s) => s.key === m.slot)?.label}</div>
                      <div className={`line-clamp-3 text-sm leading-snug ${r ? "font-semibold" : "italic text-ink-muted"}`}>{r?.title ?? m.label}</div>
                      {r && <div className="mt-1 flex items-center gap-1.5 text-xs text-ink-muted"><span className="h-1.5 w-1.5 rounded-full" style={{ background: tokenColour(c) }} />for {m.servings ?? r.servings ?? "?"}{m.added_to_list_at && <ShoppingBasket className="ml-auto h-3.5 w-3.5 text-good" />}</div>}
                    </button>
                  );
                })}
                <button type="button" onClick={() => setEdit({ plan_on: key, slot: "dinner" })} className="mt-auto flex items-center justify-center gap-1 rounded-xl border border-dashed border-line py-2 text-xs text-ink-muted hover:border-primary/50 hover:text-ink"><Plus className="h-3.5 w-3.5" />Add</button>
              </div>
            </div>
          );
        })}
      </div>
      {(recipes.data ?? []).length === 0 && <p className="mt-6 text-sm text-ink-muted">Tip: add recipes to the <Link to="/kitchen/recipes" className="font-semibold text-primary">recipe book</Link> first, then plan them onto days here.</p>}

      {edit && <MealSheet m={edit} days={days} recipes={recipes.data ?? []} onClose={() => setEdit(null)} />}
      {building && <ShopSheet meals={list} recipes={byId} onClose={() => setBuilding(false)} />}
    </>
  );
}

function MealSheet({ m, days, recipes, onClose }: { m: Partial<Meal> & { plan_on: string }; days: Date[]; recipes: Recipe[]; onClose: () => void }) {
  const save = useSaveMeal();
  const [day, setDay] = useState(m.plan_on); const [slot, setSlot] = useState<Slot>(m.slot ?? "dinner");
  const [recipeId, setRecipeId] = useState<string | null>(m.recipe_id ?? null); const [label, setLabel] = useState(m.label ?? "");
  const [servings, setServings] = useState<number>(m.servings ?? recipes.find((r) => r.id === m.recipe_id)?.servings ?? 4);
  const [q, setQ] = useState(""); const [err, setErr] = useState<string | null>(null);
  const shown = recipes.filter((r) => !q.trim() || r.title.toLowerCase().includes(q.trim().toLowerCase())).sort((a, b) => Number(b.favourite) - Number(a.favourite) || a.title.localeCompare(b.title));
  const pick = (r: Recipe) => { setRecipeId(r.id); setLabel(""); setServings(r.servings ?? servings); };
  const submit = () => {
    if (!recipeId && !label.trim()) { setErr("Pick a recipe or type what you're having."); return; }
    save.mutate({ id: m.id, plan_on: day, slot, recipe_id: recipeId, label: recipeId ? null : label.trim(), servings: recipeId ? servings : null }, { onSuccess: onClose, onError: (e) => setErr(e.message) });
  };
  return (
    <Sheet title={m.id ? "Change meal" : "Plan a meal"} onClose={onClose}>
      <div className="space-y-5">
        <div className="grid grid-cols-7 gap-1">
          {days.map((d) => (
            <button key={ymd(d)} type="button" onClick={() => setDay(ymd(d))} className={`flex flex-col items-center rounded-xl py-2 text-xs ${day === ymd(d) ? "bg-primary font-semibold text-primary-ink" : "bg-surface-raised"}`}>
              {format(d, "EEE")}<span className="text-base font-semibold">{format(d, "d")}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-1.5 rounded-full bg-surface-raised p-1">
          {SLOTS.map((s) => <button key={s.key} type="button" onClick={() => setSlot(s.key)} className={`flex-1 rounded-full py-1.5 text-sm ${slot === s.key ? "bg-surface font-semibold shadow-sm" : "text-ink-muted"}`}>{s.label}</button>)}
        </div>

        <div>
          <span className="text-sm font-medium text-ink-muted">From the recipe book</span>
          <div className="relative mt-1.5">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
            <input className={`${inputCls} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a recipe" />
          </div>
          <ul className="mt-2 max-h-56 overflow-y-auto rounded-xl ring-1 ring-line">
            {shown.length === 0 && <li className="p-3 text-sm text-ink-muted">No recipes match.</li>}
            {shown.map((r) => (
              <li key={r.id}><button type="button" onClick={() => pick(r)} className={`flex w-full items-center gap-2 border-b border-line/60 px-3 py-2.5 text-left text-sm last:border-0 ${recipeId === r.id ? "bg-primary/10 font-semibold text-primary" : "hover:bg-surface-raised"}`}>
                <ChefHat className="h-4 w-4 shrink-0" /><span className="min-w-0 flex-1 truncate">{r.title}</span>{r.servings && <span className="text-xs text-ink-muted">serves {r.servings}</span>}
              </button></li>
            ))}
          </ul>
        </div>

        {recipeId ? (
          <div className="flex items-center justify-between rounded-xl bg-surface-raised p-3">
            <span className="text-sm">How many is it for?</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setServings(Math.max(1, servings - 1))} aria-label="Fewer" className="rounded-full bg-surface p-1.5 ring-1 ring-line"><Minus className="h-4 w-4" /></button>
              <b className="num w-8 text-center text-lg">{servings}</b>
              <button type="button" onClick={() => setServings(Math.min(50, servings + 1))} aria-label="More" className="rounded-full bg-surface p-1.5 ring-1 ring-line"><Plus className="h-4 w-4" /></button>
            </div>
          </div>
        ) : null}

        <div>
          <span className="text-sm font-medium text-ink-muted">Or something else</span>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {QUICK.map((t) => <button key={t} type="button" onClick={() => { setLabel(t); setRecipeId(null); }} className={`rounded-full px-3 py-1.5 text-sm ${!recipeId && label === t ? "bg-ink font-semibold text-bg" : "bg-surface-raised"}`}>{t}</button>)}
          </div>
          <input className={`${inputCls} mt-2`} value={recipeId ? "" : label} onChange={(e) => { setLabel(e.target.value); setRecipeId(null); }} placeholder="Type it: tacos, BBQ at Sarah's…" />
        </div>

        <ErrorText>{err}</ErrorText>
        <div className="flex gap-2">
          <button type="button" onClick={submit} disabled={save.isPending} className="flex-1 rounded-full bg-primary py-3 font-semibold text-primary-ink disabled:opacity-50">{m.id ? "Save" : "Add to the plan"}</button>
          {m.id && <button type="button" onClick={() => save.mutate({ id: m.id, plan_on: m.plan_on, remove: true }, { onSuccess: onClose })} className="inline-flex items-center gap-1.5 rounded-full bg-surface-raised px-4 py-3 text-sm font-semibold text-bad"><Trash2 className="h-4 w-4" />Remove</button>}
        </div>
      </div>
    </Sheet>
  );
}

/** The week's ingredients, scaled and joined up, ready to tick onto the list. */
function ShopSheet({ meals, recipes, onClose }: { meals: Meal[]; recipes: Map<string, Recipe>; onClose: () => void }) {
  const shopping = useShopping(false); const add = useAddShopping(); const mark = useMarkMealsListed();
  const [again, setAgain] = useState(false);
  const use = meals.filter((m) => m.recipe_id && recipes.has(m.recipe_id) && (again || !m.added_to_list_at));
  const onList = new Set((shopping.data ?? []).filter((i) => !i.checked_at).map((i) => i.name.trim().toLowerCase()));
  const lines = useMemo(() => {
    const map = new Map<string, { item: string; qtys: string[]; section: Section; from: string[]; recipe_id: string }>();
    for (const m of use) {
      const r = recipes.get(m.recipe_id!)!; const by = r.servings && m.servings ? m.servings / r.servings : 1;
      for (const i of r.ingredients) {
        const k = i.item.trim().toLowerCase(); const q = scaleQty(i.qty, by);
        const cur = map.get(k) ?? { item: i.item.trim(), qtys: [], section: i.section ?? guessSection(i.item), from: [], recipe_id: r.id };
        if (q) cur.qtys.push(q);
        if (!cur.from.includes(r.title)) cur.from.push(r.title);
        map.set(k, cur);
      }
    }
    return [...map.values()].sort((a, b) => a.section.localeCompare(b.section) || a.item.localeCompare(b.item));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [use.map((m) => m.id).join(), recipes]);
  const [off, setOff] = useState<Set<string>>(() => new Set());
  const isOn = (l: { item: string }) => !off.has(l.item.toLowerCase()) && !(onList.has(l.item.toLowerCase()) && !off.has(`+${l.item.toLowerCase()}`));
  const toggle = (l: { item: string }) => {
    const k = l.item.toLowerCase(); const s = new Set(off);
    if (onList.has(k)) { if (s.has(`+${k}`)) s.delete(`+${k}`); else s.add(`+${k}`); }
    else if (s.has(k)) s.delete(k); else s.add(k);
    setOff(s);
  };
  const picked = lines.filter(isOn);
  const submit = () => {
    add.mutate(picked.map((l) => ({ name: l.item, qty: l.qtys.join(" + ") || null, section: l.section, recipe_id: l.recipe_id })), {
      onSuccess: () => mark.mutate(use.map((m) => m.id), { onSuccess: onClose }),
    });
  };
  return (
    <Sheet title="Shop for this week" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-ink-muted">From {use.length} {use.length === 1 ? "meal" : "meals"}, scaled to how many each is for. Untick what's in the pantry.</p>
        {meals.some((m) => m.added_to_list_at) && (
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={again} onChange={(e) => setAgain(e.target.checked)} className="h-4 w-4" />Include meals already sent to the list</label>
        )}
        {lines.length === 0 && <p className="rounded-xl bg-surface-raised p-4 text-sm text-ink-muted">Nothing new to add. The planned recipes are already on the list, or they don't have ingredients written in yet.</p>}
        <ul className="max-h-[55vh] space-y-1 overflow-y-auto">
          {lines.map((l) => (
            <li key={l.item}>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-surface-raised">
                <input type="checkbox" checked={isOn(l)} onChange={() => toggle(l)} className="mt-0.5 h-4 w-4" />
                <span className="min-w-0 flex-1 text-sm">
                  {l.qtys.length > 0 && <b className="num">{l.qtys.join(" + ")} </b>}{l.item}
                  {onList.has(l.item.toLowerCase()) && <span className="ml-1.5 text-xs text-good">already on the list</span>}
                  <span className="block truncate text-xs text-ink-muted">{l.from.join(", ")}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
        <button type="button" onClick={submit} disabled={!picked.length || add.isPending} className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-ink disabled:opacity-50">
          <UtensilsCrossed className="h-4 w-4" />Add {picked.length} to the shopping list
        </button>
      </div>
    </Sheet>
  );
}
